import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { LogisticsCatalogs, MaterialRequestsService, errMsg } from '../logistics.service';

export interface RequestFormData { scopePlaces: string[] | null; }

/**
 * Nueva solicitud de material. Quien la registra queda como "Solicita" (lo
 * pone el servidor), se avisa por correo a mesa de control y ya no se edita.
 */
@Component({
  selector: 'app-request-form-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule,
    MatIconModule, MatTooltipModule, MatDatepickerModule, FormDialogShellComponent, AutoSelectComponent],
  styleUrl: '../logistics-dialog.scss',
  template: `
<app-form-dialog-shell icon="playlist_add" heading="Nueva solicitud de material"
  [saving]="saving()" saveLabel="Registrar solicitud" saveIcon="send"
  formId="request-form" (cancelled)="ref.close(false)">
<form id="request-form" (ngSubmit)="save()">
  <div class="form-grid">
    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Solicita</mat-label>
      <input matInput name="requestedBy" [value]="requestedBy" readonly>
      <mat-icon matSuffix>person</mat-icon>
    </mat-form-field>

    <app-auto-select label="Hospital" name="place" [(ngModel)]="place" (changed)="onPlace()"
      [options]="placeOptions" [labelFn]="placeLabel"></app-auto-select>

    <mat-form-field appearance="outline">
      <mat-label>Se necesita para</mat-label>
      <input matInput [matDatepicker]="dp" name="neededBy" [(ngModel)]="neededBy" [min]="today">
      <mat-datepicker-toggle matSuffix [for]="dp"></mat-datepicker-toggle>
      <mat-datepicker #dp></mat-datepicker>
    </mat-form-field>

    <div class="section-title">
      <mat-icon>inventory_2</mat-icon> Material solicitado
      <span class="spacer"></span>
      <button mat-stroked-button type="button" (click)="addLine()"><mat-icon>add</mat-icon> Agregar código</button>
    </div>
    <div class="lines">
      @for (ln of lines; track ln.key; let i = $index) {
        <div class="line-row">
          <app-auto-select [label]="'Código ' + (i + 1)" [name]="'p' + ln.key" [(ngModel)]="ln.product"
            [options]="cat.products" [labelFn]="cat.productLabel"></app-auto-select>
          <mat-form-field appearance="outline">
            <mat-label>Piezas</mat-label>
            <input matInput type="number" min="1" step="1" [name]="'q' + ln.key" [(ngModel)]="ln.quantity">
          </mat-form-field>
          <button mat-icon-button type="button" color="warn" matTooltip="Quitar" (click)="lines.splice(i, 1)" [disabled]="lines.length === 1">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      }
    </div>

    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Notas</mat-label>
      <textarea matInput rows="2" name="notes" [(ngModel)]="notes" maxlength="1000"></textarea>
    </mat-form-field>

    <p class="hint-box full-col"><mat-icon>info</mat-icon>
      <span>Una vez registrada ya no se puede editar.</span></p>
  </div>
</form>
</app-form-dialog-shell>
  `,
})
export class RequestFormDialogComponent {
  readonly data = inject<RequestFormData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<RequestFormDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(MaterialRequestsService);
  private readonly msg = inject(CustomMessageService);

  readonly saving = signal(false);
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  /** Lo que se ve; el API guarda el nombre del usuario del token. */
  readonly requestedBy = sessionStorage.getItem('userName') || sessionStorage.getItem('userEmail') || '';

  readonly placeLabel = (p: any): string => String(p?.alias || p?.name || '');
  readonly warehouseLabel = (w: any): string => String(w?.name || '');

  place: string | null = null;
  warehouse: string | null = null;
  neededBy: Date | null = null;
  notes = '';
  private seq = 0;
  lines: { key: number; product: string | null; quantity: number }[] = [];

  /** Hospitales de los parámetros del usuario (solo tipo hospital), por alias. */
  readonly placeOptions = this.cat.places
    .filter(p => (!this.data.scopePlaces || this.data.scopePlaces.includes(String(p.id))) &&
      this.cat.sourceKind({ place: p.id }) === 'HOSPITAL')
    .sort((a, b) => this.placeLabel(a).localeCompare(this.placeLabel(b)));

  /** Solo lockers (no in-house) del hospital elegido. */
  get warehouseOptions(): any[] {
    return this.cat.warehouses.filter(w => w.place === this.place && !this.cat.isInhouse(w))
      .sort((a, b) => this.warehouseLabel(a).localeCompare(this.warehouseLabel(b)));
  }

  constructor() {
    this.addLine();
    if (this.placeOptions.length === 1) { this.place = this.placeOptions[0].id; this.onPlace(); }
  }

  addLine(): void { this.lines.push({ key: ++this.seq, product: null, quantity: 1 }); }

  onPlace(): void { /* el destino lo define la entrada de delivery */ }

  async save(): Promise<void> {
    const lines = this.lines.filter(l => l.product).map(l => {
      const p = this.cat.productsById[l.product!];
      return { product: l.product, code: p?.code ?? '', description: p?.description ?? '',
               quantity: Math.max(1, Math.floor(Number(l.quantity) || 1)), supplied: 0 };
    });
    const problem = !this.place ? 'Elige el hospital.'
      : !lines.length ? 'Agrega al menos un código.'
      : new Set(lines.map(l => l.product)).size !== lines.length ? 'Hay códigos repetidos: junta las piezas en un solo renglón.'
      : null;
    if (problem) { await this.msg.open({ title: 'Revisa la solicitud', type: 'warning', message: problem, dismissBtn: true }); return; }

    this.saving.set(true);
    try {
      const r: any = await this.service.create({
        place: this.place, warehouse: this.warehouse || null,
        neededBy: this.neededBy ? localDay(this.neededBy) : null,
        lines, notes: this.notes.trim() || null, status: 'Nueva',
      });
      const folio = r?.data?.[0]?.folio ?? '';
      await this.msg.open({ title: '¡Registrada!', type: 'success', message: `Solicitud <b>${folio}</b> registrada. Se avisó a mesa de control.`, duration: 2600 });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo guardar la solicitud.'), dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
